/**
 * theme.js — Global JavaScript for the DV Shopify Theme
 *
 * Covers shared utilities and behaviors that are NOT handled by section-level
 * inline scripts:
 *
 *   1. CartAPI          — Promise-based Cart AJAX helpers
 *   2. Cart count badge — Listens for cart:updated / cart:item-added events
 *   3. SectionRenderer  — Section Rendering API helper
 *   4. Smooth scroll    — Anchor links on the same page
 *   5. Utilities        — debounce() and throttle()
 *   6. Alpine cart store — Reactive $store.cart wrapping CartAPI
 *
 * V2 notes:
 *   - Announcement bar dismiss removed — handled by Alpine component.
 *   - LazyLoader removed — native loading="lazy" is used on all image_tag calls.
 *
 * Authoring notes:
 *   - Vanilla ES6+ — no frameworks, no jQuery.
 *   - Wrapped in an IIFE to avoid any global namespace pollution.
 *   - Custom events are dispatched on `document` so every component can
 *     subscribe without holding a direct reference to the emitter.
 *   - All fetch calls include the Shopify cart token header so they work
 *     correctly whether or not the storefront uses a custom token.
 */

(function () {
  'use strict';

  /* =========================================================================
     1. UTILITY FUNCTIONS — debounce & throttle
     ========================================================================= */

  /**
   * Returns a debounced version of `fn` that delays execution until `wait` ms
   * have elapsed since the last invocation.
   *
   * @param {Function} fn   - The function to debounce.
   * @param {number}   wait - Delay in milliseconds.
   * @returns {Function}
   */
  function debounce(fn, wait) {
    var timer;
    return function () {
      var ctx  = this;
      var args = arguments;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(ctx, args);
      }, wait);
    };
  }

  /**
   * Returns a throttled version of `fn` that executes at most once per `limit`
   * ms, regardless of how many times it is called in that window.
   *
   * @param {Function} fn    - The function to throttle.
   * @param {number}   limit - Minimum interval in milliseconds.
   * @returns {Function}
   */
  function throttle(fn, limit) {
    var lastRun = 0;
    return function () {
      var now = Date.now();
      if (now - lastRun < limit) return;
      lastRun = now;
      fn.apply(this, arguments);
    };
  }

  /* =========================================================================
     2. CART API — Promise-based helpers for the Shopify Cart AJAX API
     ========================================================================= */

  /**
   * CartAPI
   *
   * Every method returns a native Promise that resolves with the parsed JSON
   * response body, or rejects with an Error whose message is the cart API
   * error description (or the HTTP status text as a fallback).
   *
   * Usage:
   *   CartAPI.add([{ id: variantId, quantity: 1 }])
   *     .then(function (cart) { console.log(cart.item_count); })
   *     .catch(function (err) { console.error(err.message); });
   */
  var CartAPI = (function () {

    /**
     * Read the Shopify cart token from the page cookie so that cross-origin
     * fetch requests are correctly tied to the active session.  Returns an
     * empty string when the cookie is absent (e.g. headless or pre-render).
     *
     * @returns {string}
     */
    function getCartToken() {
      var match = document.cookie.match(/cart=([^;]+)/);
      return match ? decodeURIComponent(match[1]) : '';
    }

    /**
     * Shared fetch wrapper.
     *
     * @param {string} url     - Relative Shopify Cart API endpoint.
     * @param {Object} options - fetch() options (method, body, etc.)
     * @returns {Promise<Object>}
     */
    function request(url, options) {
      var defaults = {
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-Shopify-Cart-Token': getCartToken(),
        },
        credentials: 'same-origin',
      };

      var merged = Object.assign({}, defaults, options, {
        headers: Object.assign({}, defaults.headers, (options && options.headers) || {}),
      });

      return fetch(url, merged).then(function (response) {
        return response.json().then(function (data) {
          if (!response.ok) {
            /* Shopify AJAX errors carry a human-readable `description` field */
            var message = (data && (data.description || data.message)) || response.statusText;
            var error   = new Error(message);
            error.status = response.status;
            error.data   = data;
            throw error;
          }
          return data;
        });
      });
    }

    return {
      /**
       * Fetch the current cart object.
       * GET /cart.js
       *
       * @returns {Promise<Object>} Shopify cart object
       */
      get: function () {
        return request('/cart.js', { method: 'GET' });
      },

      /**
       * Add one or more line items to the cart.
       * POST /cart/add.js
       *
       * @param {Array<{id: number|string, quantity: number, properties?: Object}>} items
       * @returns {Promise<Object>} Shopify items_added object
       */
      add: function (items) {
        return request('/cart/add.js', {
          method: 'POST',
          body: JSON.stringify({ items: items }),
        });
      },

      /**
       * Change the quantity of a specific line item by key.
       * POST /cart/change.js
       *
       * @param {string} key      - Line item key (variant id or line item key string)
       * @param {number} quantity - New quantity (0 removes the item)
       * @returns {Promise<Object>} Updated Shopify cart object
       */
      change: function (key, quantity) {
        return request('/cart/change.js', {
          method: 'POST',
          body: JSON.stringify({ id: key, quantity: quantity }),
        });
      },

      /**
       * Update multiple line item quantities at once.
       * POST /cart/update.js
       *
       * @param {Object} updates - Map of variant id / line item key → quantity
       * @returns {Promise<Object>} Updated Shopify cart object
       */
      update: function (updates) {
        return request('/cart/update.js', {
          method: 'POST',
          body: JSON.stringify({ updates: updates }),
        });
      },
    };
  })();

  /* =========================================================================
     3. CART COUNT BADGE UPDATER
     ========================================================================= */

  /**
   * Updates every element on the page that carries [data-cart-count] whenever
   * either of the following custom events is dispatched on `document`:
   *
   *   cart:updated   — dispatched after a cart/update.js or cart/change.js call
   *   cart:item-added — dispatched after a successful cart/add.js call
   *
   * Expected event.detail shape (either event):
   *   { cart: { item_count: Number } }
   *
   * If `detail.cart` is not provided the handler fetches the cart itself so
   * callers can dispatch without a fresh cart object in hand.
   */
  (function initCartCountBadge() {

    function updateBadges(itemCount) {
      var badges = document.querySelectorAll('[data-cart-count]');
      badges.forEach(function (badge) {
        badge.textContent = itemCount;

        /* Toggle a modifier class so CSS can hide the badge at zero */
        if (itemCount > 0) {
          badge.removeAttribute('hidden');
          badge.classList.remove('site-header__cart-badge--empty');
        } else {
          badge.setAttribute('hidden', '');
          badge.classList.add('site-header__cart-badge--empty');
        }
      });

      /* Keep aria-label on the parent cart link in sync */
      var cartLinks = document.querySelectorAll('.site-header__cart-btn');
      cartLinks.forEach(function (link) {
        if (itemCount === 0) {
          link.setAttribute('aria-label', link.dataset.labelEmpty || 'Cart — empty');
        } else {
          var labelTemplate = link.dataset.labelCount || 'Cart — {count} items';
          link.setAttribute('aria-label', labelTemplate.replace('{count}', itemCount));
        }
      });
    }

    function onCartEvent(event) {
      var detail = (event && event.detail) || {};

      if (detail.cart && typeof detail.cart.item_count === 'number') {
        updateBadges(detail.cart.item_count);
      } else {
        /* No cart object provided — fetch it */
        CartAPI.get()
          .then(function (cart) { updateBadges(cart.item_count); })
          .catch(function () { /* silent — badge will update on next navigation */ });
      }
    }

    document.addEventListener('cart:updated',    onCartEvent);
    document.addEventListener('cart:item-added', onCartEvent);
  })();

  /* =========================================================================
     4. SECTION RENDERING API HELPER
     ========================================================================= */

  /**
   * SectionRenderer
   *
   * Uses the Shopify Section Rendering API to fetch fresh HTML for one or
   * more sections without a full page reload.
   *
   * Usage:
   *   SectionRenderer.render('cart-drawer')
   *     .then(function (html) {
   *       document.getElementById('cart-drawer').innerHTML = html;
   *     });
   *
   *   // Fetch multiple sections in one request:
   *   SectionRenderer.renderAll(['cart-drawer', 'header'])
   *     .then(function (sections) {
   *       // sections is { 'cart-drawer': '<html>', 'header': '<html>' }
   *     });
   */
  var SectionRenderer = (function () {

    /**
     * Fetch the rendered HTML for a single section.
     *
     * @param {string} sectionId - The section id (matches data-section-id in the DOM)
     * @param {string} [url]     - Optional page URL to render the section in context of.
     *                             Defaults to the current page URL.
     * @returns {Promise<string>} The inner HTML string for the section
     */
    function render(sectionId, url) {
      var baseUrl = url || window.location.pathname;
      var sep     = baseUrl.indexOf('?') !== -1 ? '&' : '?';
      var endpoint = baseUrl + sep + 'sections=' + encodeURIComponent(sectionId);

      return fetch(endpoint, {
        method: 'GET',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'same-origin',
      })
        .then(function (response) {
          if (!response.ok) {
            throw new Error('SectionRenderer: request failed — ' + response.status);
          }
          return response.json();
        })
        .then(function (data) {
          if (!data[sectionId]) {
            throw new Error('SectionRenderer: section "' + sectionId + '" not found in response');
          }
          return data[sectionId];
        });
    }

    /**
     * Fetch multiple sections in a single request.
     *
     * @param {string[]} sectionIds - Array of section ids
     * @param {string}  [url]       - Optional page URL context
     * @returns {Promise<Object>}   Map of sectionId → HTML string
     */
    function renderAll(sectionIds, url) {
      var baseUrl  = url || window.location.pathname;
      var sep      = baseUrl.indexOf('?') !== -1 ? '&' : '?';
      var endpoint = baseUrl + sep + 'sections=' + sectionIds.map(encodeURIComponent).join('%2C');

      return fetch(endpoint, {
        method: 'GET',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'same-origin',
      })
        .then(function (response) {
          if (!response.ok) {
            throw new Error('SectionRenderer: request failed — ' + response.status);
          }
          return response.json();
        });
    }

    return {
      render:    render,
      renderAll: renderAll,
    };
  })();

  /* =========================================================================
     5. SMOOTH SCROLL — anchor links that target an element on the same page
     ========================================================================= */

  (function initSmoothScroll() {

    /* Only activate when the user has not requested reduced motion */
    var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return;

    document.addEventListener('click', function (event) {
      /* Walk up the DOM from the click target to find an <a> element */
      var target = event.target;
      while (target && target !== document) {
        if (target.tagName === 'A') break;
        target = target.parentNode;
      }

      if (!target || target.tagName !== 'A') return;

      var href = target.getAttribute('href');
      if (!href || href.charAt(0) !== '#' || href.length < 2) return;

      /* Make sure the link points to the current page (not another page + hash) */
      var linkOrigin   = target.origin || (target.protocol + '//' + target.host);
      var windowOrigin = window.location.origin;
      if (linkOrigin && linkOrigin !== windowOrigin) return;

      var anchor = document.getElementById(href.slice(1));
      if (!anchor) return;

      event.preventDefault();

      anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });

      /* Update the URL hash without triggering a jump scroll */
      if (window.history && window.history.pushState) {
        window.history.pushState(null, '', href);
      }

      /* Move keyboard focus to the anchor target for accessibility */
      if (!anchor.hasAttribute('tabindex')) {
        anchor.setAttribute('tabindex', '-1');
      }
      anchor.focus({ preventScroll: true });
    });
  })();

  /* =========================================================================
     ALPINE.JS CART STORE
     Wraps CartAPI with reactive state for Alpine components.
     Usage in templates: $store.cart.items, $store.cart.item_count, etc.
     ========================================================================= */

  document.addEventListener('alpine:init', function () {
    Alpine.store('cart', {
      items: [],
      item_count: 0,
      total_price: 0,
      currency: 'USD',
      loading: false,
      error: null,

      /**
       * Fetch the full cart and update reactive state.
       */
      fetch: function () {
        var self = this;
        self.loading = true;
        self.error = null;

        return CartAPI.get()
          .then(function (cart) {
            self.items = cart.items || [];
            self.item_count = cart.item_count || 0;
            self.total_price = cart.total_price || 0;
            self.currency = cart.currency || 'USD';
            self.loading = false;
            return cart;
          })
          .catch(function (err) {
            self.error = err.message;
            self.loading = false;
            throw err;
          });
      },

      /**
       * Add items to cart.
       * @param {Array} items - [{id, quantity, properties}]
       */
      add: function (items) {
        var self = this;
        self.loading = true;
        self.error = null;

        return CartAPI.add(items)
          .then(function () {
            return self.fetch();
          })
          .then(function (cart) {
            document.dispatchEvent(new CustomEvent('cart:item-added', {
              bubbles: true,
              detail: { cart: cart }
            }));
            return cart;
          })
          .catch(function (err) {
            self.error = err.message;
            self.loading = false;
            throw err;
          });
      },

      /**
       * Change quantity of a line item.
       * @param {string} key - Line item key
       * @param {number} quantity - New quantity (0 to remove)
       */
      change: function (key, quantity) {
        var self = this;
        self.loading = true;
        self.error = null;

        return CartAPI.change(key, quantity)
          .then(function (cart) {
            self.items = cart.items || [];
            self.item_count = cart.item_count || 0;
            self.total_price = cart.total_price || 0;
            self.loading = false;

            document.dispatchEvent(new CustomEvent('cart:updated', {
              bubbles: true,
              detail: { cart: cart }
            }));
            return cart;
          })
          .catch(function (err) {
            self.error = err.message;
            self.loading = false;
            throw err;
          });
      },

      /**
       * Update multiple line items at once.
       * @param {Object} updates - { variantId: quantity, ... }
       */
      update: function (updates) {
        var self = this;
        self.loading = true;
        self.error = null;

        return CartAPI.update(updates)
          .then(function (cart) {
            self.items = cart.items || [];
            self.item_count = cart.item_count || 0;
            self.total_price = cart.total_price || 0;
            self.loading = false;

            document.dispatchEvent(new CustomEvent('cart:updated', {
              bubbles: true,
              detail: { cart: cart }
            }));
            return cart;
          })
          .catch(function (err) {
            self.error = err.message;
            self.loading = false;
            throw err;
          });
      },

      /**
       * Format price in cents to currency string.
       * @param {number} cents - Price in cents
       * @returns {string}
       */
      formatMoney: function (cents) {
        return '$' + (cents / 100).toFixed(2);
      }
    });

    /* Fetch initial cart state */
    Alpine.store('cart').fetch();
  });

  /* =========================================================================
     PUBLIC API — expose selected utilities on window.theme for opt-in use
     by other scripts (e.g. section JS that wants to dispatch cart events or
     call the Section Rendering API without duplicating code).
     ========================================================================= */

  window.theme = window.theme || {};
  window.theme.CartAPI = CartAPI;
  window.theme.SectionRenderer = SectionRenderer;
  window.theme.utils = {
    debounce: debounce,
    throttle: throttle,
  };

})();
